import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { fmt2 } from "@/lib/money";

const useChartTheme = () => {
  const { resolved } = useTheme();
  const dark = resolved === "dark";
  return {
    axis: { stroke: dark ? "#888" : "#525252", fontSize: 11 },
    grid: { stroke: dark ? "#333" : "#E5E5E5", strokeDasharray: "0" },
    tooltip: {
      backgroundColor: dark ? "#2A2A2A" : "#FFFFFF",
      border: dark ? "1px solid #3a3a3a" : "1px solid rgba(0,0,0,0.08)",
      borderRadius: 10,
      fontSize: "12px",
      color: dark ? "#F2F2F2" : "#1A1A1A",
      boxShadow: dark ? "0 6px 18px rgba(0,0,0,0.6)" : "0 6px 18px rgba(0,0,0,0.10)",
    },
    primary: "#C9A227",        // gold
    primarySoft: dark ? "#a48420" : "#E0B82E",
    secondary: dark ? "#B0B0B0" : "#525252",
    text: dark ? "#F2F2F2" : "#1A1A1A",
    profit: "#2ECC71",
    loss: "#E63946",
    info: "#3A86FF",
    pie: dark
      ? ["#C9A227", "#3A86FF", "#2ECC71", "#E63946", "#A48420", "#8A8A8A", "#5B6FFF", "#27A35D"]
      : ["#C9A227", "#3A86FF", "#2ECC71", "#E63946", "#8A6C19", "#525252", "#1E3A5F", "#0D7377"],
  };
};

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(0)}`;

export const ChartFrame = ({ title, subtitle, children, testid, height = 280 }) => (
  <section className="vp-card p-6 vp-fade-in" data-testid={testid}>
    {title && <h3 className="text-lg font-medium tracking-tight mb-1">{title}</h3>}
    {subtitle && <p className="text-xs vp-muted mb-4">{subtitle}</p>}
    <div
      className="w-full landscape:max-lg:max-h-[75vh]"
      style={{ height, minWidth: 1, minHeight: 1 }}
    >{children}</div>
  </section>
);

export const ChartEmpty = ({ message = "No data yet." }) => (
  <div className="flex items-center justify-center h-full text-xs vp-muted">{message}</div>
);

export const ValueLineChart = ({ data, dataKey = "value", label = "Value", color, testid }) => {
  const t = useChartTheme();
  if (!data || data.length === 0) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} data-testid={testid}>
        <CartesianGrid {...t.grid} vertical={false} />
        <XAxis dataKey="date" tick={t.axis} tickLine={false} />
        <YAxis tick={t.axis} tickLine={false} tickFormatter={fmtCurrency} width={56} />
        <Tooltip contentStyle={t.tooltip} formatter={(v) => [`$${fmt2(v)} NZD`, label]} />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color || t.primary}
          strokeWidth={2.25}
          strokeLinejoin="round"
          strokeLinecap="round"
          dot={false}
          animationDuration={400}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const ProfitLineChart = ({ data, testid }) => {
  const t = useChartTheme();
  if (!data || data.length === 0) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} data-testid={testid}>
        <CartesianGrid {...t.grid} vertical={false} />
        <XAxis dataKey="date" tick={t.axis} tickLine={false} />
        <YAxis tick={t.axis} tickLine={false} tickFormatter={fmtCurrency} width={56} />
        <Tooltip contentStyle={t.tooltip} formatter={(v) => [`$${fmt2(v)} NZD`, "Profit"]} />
        <Line
          type="monotone"
          dataKey="profit"
          stroke={t.profit}
          strokeWidth={2.25}
          strokeLinejoin="round"
          strokeLinecap="round"
          dot={{ r: 3, fill: t.profit, strokeWidth: 0 }}
          animationDuration={400}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const CategoryPieChart = ({ data, testid, legendFormatter }) => {
  const t = useChartTheme();
  if (!data || data.length === 0) return <ChartEmpty />;
  // Sum once for the optional rich legend formatter (so callers can show
  // value + percentage per slice without recomputing the total themselves).
  const total = data.reduce((s, d) => s + Number(d.value || 0), 0);
  return (
    // Flex column: chart on top (flex-1 responsive), custom legend BELOW
    // (fixed-content, shrink-0). This guarantees the legend never overlaps
    // the pie regardless of how many entries it holds, and the pie always
    // gets the remaining vertical space. Pie uses percentage-based radii
    // so the circle scales with the container at every breakpoint and
    // never clips top/bottom.
    <div className="flex flex-col h-full min-h-0" data-testid={`${testid}-layout`}>
      <div className="flex-1 min-h-0 pt-2 pb-3">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
          <PieChart data-testid={testid} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              outerRadius="78%"
              innerRadius="45%"
              stroke={t.tooltip.backgroundColor}
              strokeWidth={2}
              paddingAngle={2}
              animationDuration={400}
            >
              {data.map((_, i) => <Cell key={i} fill={t.pie[i % t.pie.length]} />)}
            </Pie>
            <Tooltip contentStyle={t.tooltip} formatter={(v) => [`$${fmt2(v)} NZD`, "Est. Value"]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul
        data-testid={`${testid}-legend`}
        className="shrink-0 flex flex-wrap justify-center gap-x-4 gap-y-1.5 pt-3 mt-1 border-t border-[hsl(var(--border))] text-xs"
        style={{ color: t.text }}
      >
        {data.map((entry, i) => {
          const label = entry?.name ?? "";
          const rendered = legendFormatter ? legendFormatter(label, entry, total) : label;
          return (
            <li key={`${label}-${i}`} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden="true"
                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: t.pie[i % t.pie.length] }}
              />
              <span>{rendered}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Tooltip for the per-category bar chart. Reads rollups directly off the row
// so Cost / Est. Value / Profit are always shown, even on the Profit chart
// where only one bar is visible.
const CategoryBarTooltip = ({ active, payload, label }) => {
  const t = useChartTheme();
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload || {};
  const cost = Number(row.total_cost ?? row.cost ?? 0);
  const value = Number(row.total_est_value ?? row.value ?? 0);
  const profit = Number(row.total_profit ?? (value - cost));
  const muted = t.tooltip.color === "#F2F2F2" ? "#B0B0B0" : "#666666";
  const rowStyle = { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 };
  const profitColor = profit >= 0 ? t.profit : t.loss;
  return (
    <div style={{ ...t.tooltip, padding: "8px 10px", minWidth: 180 }}>
      <div style={{ color: t.text, fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={rowStyle}>
        <span style={{ color: muted }}>Cost</span>
        <span style={{ color: t.text, fontWeight: 500 }}>${fmt2(cost)}</span>
      </div>
      <div style={rowStyle}>
        <span style={{ color: muted }}>Est. Value</span>
        <span style={{ color: t.text, fontWeight: 500 }}>${fmt2(value)}</span>
      </div>
      <div style={rowStyle}>
        <span style={{ color: muted }}>Profit</span>
        <span style={{ color: profitColor, fontWeight: 500 }}>${fmt2(profit)}</span>
      </div>
    </div>
  );
};

export const CategoryBarChart = ({ data, testid }) => {
  const t = useChartTheme();
  if (!data || data.length === 0) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} data-testid={testid}>
        <CartesianGrid {...t.grid} vertical={false} />
        <XAxis dataKey="name" tick={t.axis} tickLine={false} interval={0} angle={data.length > 4 ? -15 : 0} textAnchor={data.length > 4 ? "end" : "middle"} height={data.length > 4 ? 60 : 30} />
        <YAxis tick={t.axis} tickLine={false} tickFormatter={fmtCurrency} width={56} />
        <Tooltip content={<CategoryBarTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12, color: t.text }} iconType="circle" />
        <Bar dataKey="cost" fill={t.secondary} name="Cost" radius={[6, 6, 0, 0]} animationDuration={400} />
        <Bar dataKey="estimated" fill={t.primary} name="Est. Value" radius={[6, 6, 0, 0]} animationDuration={400} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const CompareBarChart = ({ data, testid }) => {
  const t = useChartTheme();
  if (!data || data.length === 0) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} layout="vertical" data-testid={testid}>
        <CartesianGrid {...t.grid} horizontal={false} />
        <XAxis type="number" tick={t.axis} tickLine={false} tickFormatter={fmtCurrency} />
        <YAxis type="category" dataKey="label" tick={t.axis} tickLine={false} width={130} />
        <Tooltip contentStyle={t.tooltip} formatter={(v) => [`$${fmt2(v)} NZD`, "Value"]} />
        <Bar dataKey="value" fill={t.primary} radius={[0, 6, 6, 0]} animationDuration={400} />
      </BarChart>
    </ResponsiveContainer>
  );
};
